import Typography from "@mui/joy/Typography";
import { memo, useState, useEffect } from "react";
import { useRecoilValue } from "recoil";
import useOpenTask from "hooks/useOpenTask";
import { userAtom } from "recoil/userAtom";
import { IncompleteChip } from "./TaskChip";
import { Box, Stack } from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import useGetTaskTitleFromTaskKey from "hooks/useGetTaskTitleFromTaskKey";
import useOpenThoughtRecord from "hooks/useOpenThoughtRecord";
import { getTaskIdFromTaskKey } from "logic/logics";
import useOpenNewThoughtRecord from "hooks/useOpenNewThoughtRecord";
import { extractHexColor } from "styles/hexOpacity";
import { theme } from "styles/theme";
import useOpenMeditationRecord from "../../hooks/useOpenMeditationRecord";
import useOpenNewMeditationRecord from "../../hooks/useOpenNewMeditationRecord";

const CheckIcon = () => {
  return (
    <svg
      width="11"
      height="8"
      viewBox="0 0 11 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 3.5L4 6.5L9.5 1"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export function Status({
  isDone,
  isOpen,
  isOptional,
}: {
  isDone: boolean;
  isOpen: boolean;
  isOptional?: boolean;
}) {
  return (
    <Stack
      sx={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        ...(!isDone
          ? {
              borderWidth: "2px",
              borderColor:
                isOpen && !isOptional
                  ? "primary.solid"
                  : theme.vars.palette.Content.Weak,
            }
          : {
              backgroundColor: theme.vars.palette.Content.Weak,
            }),
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {isDone && <CheckIcon />}
    </Stack>
  );
}

function LockIcon() {
  const color = extractHexColor(theme.vars.palette.primary.solid);
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 8V6C14 3.79086 12.2091 2 10 2V2C7.79086 2 6 3.79086 6 6V8"
        stroke={color}
        strokeWidth="2"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4 8C2.89543 8 2 8.89543 2 10V16C2 17.1046 2.89543 18 4 18H16C17.1046 18 18 17.1046 18 16V10C18 8.89543 17.1046 8 16 8H4ZM12 12C12 12.9319 11.3626 13.715 10.5 13.937V15.5C10.5 15.7761 10.2761 16 10 16C9.72386 16 9.5 15.7761 9.5 15.5V13.937C8.63739 13.715 8 12.9319 8 12C8 10.8954 8.89543 10 10 10C11.1046 10 12 10.8954 12 12Z"
        fill={color}
      />
    </svg>
  );
}

function TaskItemTemplate({
  taskKey,
  isOpen,
  isDone,
  isLocked,
  isOptional,
  hasMissingValue,
  disableDirectAction,
  linkedThoughtRecordKey,
  linkedMeditationRecordKey,
  setNumber,
  hasConnectionLine,
  connectionTop,
  connectionHeight,
  isFirstOfSet,
  isLastOfSet,
}: {
  taskKey: string;
  isOpen: boolean;
  isDone: boolean;
  isLocked?: boolean;
  isOptional?: boolean;
  hasMissingValue?: boolean;
  disableDirectAction?: boolean;
  linkedThoughtRecordKey?: string;
  linkedMeditationRecordKey?: string;
  setNumber?: number;
  hasConnectionLine?: boolean;
  connectionTop?: number;
  connectionHeight?: number;
  isFirstOfSet?: boolean;
  isLastOfSet?: boolean;
}) {
  const openTask = useOpenTask({ taskKey: taskKey });
  const openThoughtRecord = useOpenThoughtRecord(linkedThoughtRecordKey);
  const openMeditationRecord = useOpenMeditationRecord(
    linkedMeditationRecordKey
  );
  const openNewNegativeThoughtRecord = useOpenNewThoughtRecord("negative");
  const openNewPositiveThoughtRecord = useOpenNewThoughtRecord("positive");
  const openNewMeditationRecord = useOpenNewMeditationRecord();
  const openPayment = useOpenTask({ taskKey: "payment" });
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmModalType, setConfirmModalType] =
    useState<string>("생각기록지");
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);

  // 잠금 해제 타이밍 지연
  const [displayLocked, setDisplayLocked] = useState(isLocked);

  useEffect(() => {
    if (isLocked) {
      setDisplayLocked(true);
    } else {
      // isLocked가 false가 되면 400ms 후 UI 업데이트
      const timer = setTimeout(() => {
        setDisplayLocked(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isLocked]);

  const title = useGetTaskTitleFromTaskKey(taskKey);
  return (
    <Stack
      direction="row"
      alignItems={"center"}
      sx={{
        position: "relative",
        cursor: "pointer",
        px: "12px",
        py: "8px",
        borderRadius: "12px",
        "@media (min-width: 1024px)": {
          "&:hover": {
            backgroundColor: theme.vars.palette.Surface.Backward,
          },
        },
        "@media (max-width: 1024px)": {
          "&:hover": {
            backgroundColor: theme.vars.palette.Surface.Forward,
          },
        },
        "&:active": {
          backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
        },
        "&:focus": {
          backgroundColor: theme.vars.palette.Surface.Forward,
        },
      }}
      onClick={() => {
        (document.activeElement as HTMLElement)?.blur();
        if (!disableDirectAction) {
          if (isLocked) {
            const taskId = getTaskIdFromTaskKey(taskKey);
            // 0-10-A, 0-11-A, 0-20-A는 선행 활동지 미완료 안내 표시
            if (taskId === "0-11-A" || taskId === "0-20-A") {
              setIsLockedModalOpen(true);
            } else {
              openPayment();
            }
          } else {
            if (
              getTaskIdFromTaskKey(taskKey) === "0-7-A" ||
              getTaskIdFromTaskKey(taskKey) === "7-2-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-5-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-7-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-9-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-11-A"
            ) {
              const taskId = getTaskIdFromTaskKey(taskKey);
              const proceed = linkedThoughtRecordKey
                ? openThoughtRecord
                : () => {
                    if (taskId === "7-2-A") {
                      openNewPositiveThoughtRecord();
                    } else {
                      openNewNegativeThoughtRecord();
                    }
                  };

              if (!isDone && !isCoach) {
                setConfirmModalType("생각기록지");
                setConfirmAction(() => proceed);
                setIsConfirmOpen(true);
              } else {
                proceed();
              }
            } else if (getTaskIdFromTaskKey(taskKey).includes("90-1-A")) {
              const proceed = linkedMeditationRecordKey
                ? openMeditationRecord
                : isCoach
                  ? null
                  : openNewMeditationRecord;

              if (!proceed) {
                return;
              }

              if (!isDone && !isCoach) {
                setConfirmModalType("명상훈련지");
                setConfirmAction(() => proceed);
                setIsConfirmOpen(true);
              } else {
                proceed();
              }
            } else {
              openTask();
            }
          }
        }
      }}
    >
      <Stack
        direction="row"
        alignItems={"center"}
        spacing={"8px"}
        sx={{ flex: 1 }}
      >
        <Box sx={{ position: "relative", width: "20px", height: "20px" }}>
          <Box
            sx={{
              position: "absolute",
              opacity: isLocked ? 1 : 0,
              transition: "opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <LockIcon />
          </Box>
          <Box
            sx={{
              position: "absolute",
              opacity: isLocked ? 0 : 1,
              transition: "opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Status isOpen={isOpen} isDone={isDone} isOptional={isOptional} />
          </Box>
        </Box>
        {hasConnectionLine && (
          <Box
            sx={{
              width: "8px",
              height: `${connectionHeight}px`,
              position: "absolute",
              top: `${connectionTop}px`,
              left: "-16px",
              borderRadius: `${isFirstOfSet ? 4 : 0}px 0px 0px ${
                isLastOfSet ? 4 : 0
              }px`,
              borderWidth: `1px 0px ${isLastOfSet ? 1 : 0}px 1px`,
              borderColor: theme.vars.palette.Content.SuperWeak,
            }}
          ></Box>
        )}

        <Typography
          variant={"Body"}
          sx={{
            flex: 1,
            fontWeight: 500,
            color: theme.vars.palette.Content.Strong,
            px: 0,
          }}
        >
          {title}
        </Typography>
      </Stack>

      {isCoach && hasMissingValue && <IncompleteChip />}

      <CustomModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={`<${confirmModalType}>로 이동할게요.`}
        subtitle={"그곳에서 활동지를 계속 진행해주세요."}
        actions={[
          {
            label: "확인",
            onClick: () => {
              const action = confirmAction;
              setIsConfirmOpen(false);
              setConfirmAction(null);
              if (action) action();
            },
            variant: "Solid",
          },
        ]}
      />

      <CustomModal
        open={isLockedModalOpen}
        onClose={() => setIsLockedModalOpen(false)}
        title="이전 활동지를 먼저 완료해주세요"
        subtitle={
          getTaskIdFromTaskKey(taskKey) === "0-11-A"
            ? "<맞춤 프로그램 구성하기 (1)>부터 진행해주세요."
            : getTaskIdFromTaskKey(taskKey) === "0-20-A"
              ? "<맞춤 프로그램 구성하기 (2)>부터 진행해주세요."
              : "선행 활동지를 진행해주세요."
        }
        actions={[
          {
            label: "확인",
            onClick: () => {
              setIsLockedModalOpen(false);
            },
            variant: "Solid",
          },
        ]}
      />
    </Stack>
  );
}

export default memo(TaskItemTemplate);
